import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useToast } from '@kanda-libs/ks-design-library';
import {
  BusinessConfig,
  LenderConfig,
  actions,
} from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/shared/App';
import useApiError from 'hooks/useApiError';
import { selectors } from 'store';
import { reloadInitialData } from 'store/slices/app';

export interface CompanyRateRequestHook {
  isLoading: boolean;
  isSubmitting: boolean;
  fca?: string;
  lenderConfigs?: LenderConfig[];
  businessConfig?: BusinessConfig;
  toAdd?: string[];
  toRemove?: string[];
  onClick: () => void;
}

export default function useCompanyRateRequest(): CompanyRateRequestHook {
  const dispatch = useAppDispatch();
  const onError = useApiError('Unknown error updating rates');
  const { showSuccess } = useToast();

  const company = useSelector(selectors.company.getEntity);
  const companyIsLoading = useSelector(selectors.infoEntity.getIsLoading);

  const enrolment = useSelector(selectors.enrolment.getEntity);
  const enrolmentIsLoading = useSelector(selectors.enrolment.getIsLoading);

  const isSubmitting = useSelector(selectors.enrolment.getIsSubmitting);

  const isLoading = useMemo(
    () => companyIsLoading || enrolmentIsLoading,
    [companyIsLoading, enrolmentIsLoading],
  );

  const fca = useMemo(() => company?.fca_number, [company]);
  const lenderConfigs = useMemo(() => company?.lender_configs, [company]);
  const businessConfig = useMemo(() => company?.business_config, [company]);

  const toAdd = useMemo(() => {
    const add = enrolment?.rate_request?.to_add;
    if (!add || add.length === 0) return undefined;
    return add;
  }, [enrolment]);

  const toRemove = useMemo(() => {
    const remove = enrolment?.rate_request?.to_remove;
    if (!remove || remove.length === 0) return undefined;
    return remove;
  }, [enrolment]);

  const onClick = useCallback(() => {
    const id = enrolment?.id;
    if (!id) return;
    dispatch(
      actions.putEnrolment({
        body: enrolment,
        params: {
          id,
        },
        onError,
        onSuccess: () => {
          showSuccess('Rates updated');
          reloadInitialData();
        },
      }),
    );
  }, [enrolment, dispatch, onError, showSuccess]);

  return {
    isLoading,
    isSubmitting,
    fca,
    lenderConfigs,
    businessConfig,
    toAdd,
    toRemove,
    onClick,
  };
}
